import React from 'react';

export function Footer() {
  return (
    <footer id="footer" className="bg-[#0d1510] pt-16 px-8 md:px-16 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr] gap-12 mb-12 pb-12 border-b border-white/[0.08]">
          {/* Brand */}
          <div>
            <img src="/logo.png" alt="Nestlent" className="w-40 mb-5" />
            <p className="text-[0.85rem] font-light leading-[1.8] text-white/40 max-w-[280px]">
              Crafting exceptional living spaces across Kerala, where architecture and emotion meet to create homes that endure through our Vastu-integrated green building techniques.
            </p>
            {/* Social Links */}
            <div className="flex flex-col gap-4 mt-6">
              <a
                href="https://www.instagram.com/nestlent_builders/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 no-underline"
              >
                <div 
                  className="w-9 h-9 rounded-[10px] flex items-center justify-center text-white transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_15px_rgba(214,36,159,0.4)]"
                  style={{ background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[0.65rem] tracking-[0.1em] uppercase text-white/30 font-medium leading-none mb-1">Instagram</span>
                  <span className="text-[0.88rem] font-medium text-white/50 group-hover:text-white transition-colors duration-300">@nestlent_builders</span>
                </div>
              </a>

              <a
                href="https://wa.me/919037322023"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 no-underline"
              >
                <div 
                  className="w-9 h-9 rounded-[10px] flex items-center justify-center text-white bg-[#25D366] transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_15px_rgba(37,211,102,0.4)]"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[0.65rem] tracking-[0.1em] uppercase text-white/30 font-medium leading-none mb-1">WhatsApp</span>
                  <span className="text-[0.88rem] font-medium text-white/50 group-hover:text-white transition-colors duration-300">+91 90373 22023</span>
                </div>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div className="text-[0.68rem] tracking-[0.25em] uppercase text-white/30 mb-5">Quick Links</div>
            <ul className="list-none space-y-3">
              {[
                { label: 'About Us', href: '#about' },
                { label: 'Services', href: '#services' },
                { label: 'Projects', href: '#projects' },
                { label: 'Gallery', href: '#gallery' },
              ].map(l => (
                <li key={l.label}>
                  <a href={l.href} className="text-[0.85rem] font-light text-white/50 no-underline transition-colors duration-300 hover:text-nestlent-green-mid">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <div className="text-[0.68rem] tracking-[0.25em] uppercase text-white/30 mb-5">Services</div>
            <ul className="list-none space-y-3">
              {['Architectural Design', 'Interior Design', 'Master Planning', 'Consultation', 'Renovation'].map(l => (
                <li key={l}>
                  <a href="#services" className="text-[0.85rem] font-light text-white/50 no-underline transition-colors duration-300 hover:text-nestlent-green-mid">
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-[0.68rem] tracking-[0.25em] uppercase text-white/30 mb-5">Contact</div>
            <ul className="list-none space-y-4">
              <li>
                <div className="text-[0.7rem] tracking-[0.1em] uppercase text-nestlent-green-mid mb-1">Office Address</div>
                <p className="text-[0.85rem] font-light text-white/60 leading-relaxed">
                  First Floor, Therayil Complex,<br/>
                  Near Kandakurumbakkavu Temple,<br/>
                  Ponnani, Malappuram, Kerala
                </p>
              </li>
              <li>
                <div className="text-[0.7rem] tracking-[0.1em] uppercase text-nestlent-green-mid mb-1">Get in Touch</div>
                <div className="flex flex-col gap-1">
                   <a href="tel:+919037322023" className="text-[0.85rem] font-light text-white/50 no-underline hover:text-white transition-colors duration-300">+91 90373 22023</a>
                   <a href="tel:+919947847587" className="text-[0.85rem] font-light text-white/50 no-underline hover:text-white transition-colors duration-300">+91 99478 47587</a>
                  <a href="mailto:enquiry@nestlent.com" className="text-[0.85rem] font-light text-white/50 no-underline hover:text-white transition-colors duration-300">enquiry@nestlent.com</a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-[0.78rem] text-white/25">
            © {new Date().getFullYear()} <a href="#" className="text-nestlent-green-mid no-underline">Nestlent Builders & Developers</a>. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
